import { alpha, createTheme } from "@mui/material/styles";
import "./theme.types.js"; // Import type augmentations
/**
 * # The Voicely surface
 *
 * One file, because a design system that lives in `sx` props is not a design
 * system — it is a thousand independent decisions that agree by luck and drift
 * apart on the first edit. Everything visual that is decided once for the whole
 * app is decided here: the palette, the type ramp, the corner radius, the
 * shadow ramp, and the resting appearance of every MUI component the app uses.
 * A page should be able to say `<Card>` and get the house card.
 *
 * ## Teal on warm neutral
 *
 * The accent is teal (`#0D9488`) and the canvas is a warm off-white
 * (`#F8F8F6`) rather than the blue-grey that every other dashboard uses. Blue
 * was the incumbent and is the safe answer; it is also the answer this product
 * shares with the telephony console, the CRM and the billing portal that sit in
 * the operator's other tabs, and "which tab was Voicely" is a real cost. The
 * warm neutral is what keeps the teal from reading as sickly: teal on a cool
 * grey pulls green, teal on a warm grey stays teal.
 *
 * Greys are Tailwind's `stone` ramp rather than `slate`. This is not
 * decoration — a warm canvas with cool grey text is the specific combination
 * that looks like a rendering fault, so the neutrals have to be warm all the
 * way down or not at all.
 *
 * ## Borders carry the structure, not shadows
 *
 * Cards are outlined and flat by default. A dense operational screen puts
 * twenty cards on the page at once, and twenty drop shadows is visual noise
 * that makes none of them look important. Shadow is spent only on things that
 * genuinely float above the page — menus, dialogs, popovers — and on the one
 * hover state where lift is the affordance. The ramp below is deliberately
 * shallower than MUI's default for the same reason.
 */
/** Tailwind `stone`, which is the warm neutral ramp the whole theme is built on. */
const stone = {
    50: "#FAFAF9",
    100: "#F5F5F4",
    200: "#E7E5E4",
    300: "#D6D3D1",
    400: "#A8A29E",
    500: "#78716C",
    600: "#57534E",
    700: "#44403C",
    800: "#292524",
    900: "#1C1917",
    950: "#0C0A09",
};
const teal = {
    300: "#5EEAD4",
    400: "#2DD4BF",
    500: "#14B8A6",
    600: "#0D9488",
    700: "#0F766E",
    800: "#115E59",
};
/**
 * The shadow ramp, warm-tinted and shallow.
 *
 * MUI's default ramp is a cool near-black at fairly high alpha, which on a warm
 * canvas reads as a dirty grey halo rather than as depth. These are tinted with
 * the text colour instead, so a raised surface looks like it is lit by the same
 * light as everything else.
 *
 * Only 0–8 are distinct. MUI requires 25 entries and the app has no use for
 * twenty-five depths; the tail repeats the deepest value so that a stray
 * `elevation={16}` degrades to "a dialog" rather than to a black rectangle.
 */
const buildShadows = (mode) => {
    // Dark surfaces need a genuinely black shadow at higher alpha — a tinted one
    // is invisible against a near-black canvas, and an un-shadowed menu floating
    // over a dark page has no edge at all.
    const c = mode === "light" ? "28, 25, 23" : "0, 0, 0";
    const k = mode === "light" ? 1 : 2.2;
    const s = (blur, y, a, blur2, y2, a2) => `0 ${y}px ${blur}px rgba(${c}, ${(a * k).toFixed(3)}), 0 ${y2}px ${blur2}px rgba(${c}, ${(a2 * k).toFixed(3)})`;
    const ramp = [
        "none",
        s(2, 1, 0.04, 1, 0, 0.03),
        s(3, 1, 0.05, 2, 1, 0.03),
        s(6, 2, 0.06, 2, 1, 0.03),
        s(10, 4, 0.07, 3, 1, 0.04),
        s(16, 6, 0.08, 4, 2, 0.04),
        s(24, 8, 0.09, 6, 2, 0.04),
        s(32, 12, 0.1, 8, 4, 0.04),
        s(40, 16, 0.12, 10, 4, 0.05),
    ];
    return [...ramp, ...Array(25 - ramp.length).fill(ramp[ramp.length - 1])];
};
/**
 * Scrollbars, everywhere, in one place.
 *
 * Applied through `CssBaseline` rather than per-component because a scrollbar
 * is chrome: the moment one pane styles its own and another does not, the page
 * has two different scrollbars in it and the difference reads as a rendering
 * bug rather than as a choice.
 *
 * **Thin and nearly invisible, not hidden.** `scrollbar-width: none` was the
 * obvious alternative and is the wrong one — a scrollport with no visible
 * scrollbar gives a reader no way to tell that there is more below, and no
 * way to see how much. The thumb is a low-alpha wash of the text colour that
 * lifts on hover, so it is findable when looked for and silent when not.
 *
 * Both syntaxes, deliberately. `scrollbar-width`/`scrollbar-color` is the
 * standard and is what Firefox honours; `::-webkit-scrollbar` is what Chrome
 * and Safari honour, and a browser that supports the standard properties
 * ignores the pseudo-elements rather than applying both.
 *
 * The track is transparent all the way through: a coloured track is a
 * permanent vertical rule down the edge of every scrollport, which is the
 * thing that makes a default scrollbar look heavy.
 */
const scrollbarStyles = (mode) => {
    // Off the text colour rather than a fixed grey, so the thumb sits at the
    // same distance from the background in both modes instead of vanishing in
    // one of them.
    const thumb = mode === "light" ? "rgba(28, 25, 23, 0.18)" : "rgba(250, 250, 249, 0.18)";
    const thumbHover = mode === "light" ? "rgba(28, 25, 23, 0.34)" : "rgba(250, 250, 249, 0.34)";
    return {
        "*": {
            scrollbarWidth: "thin",
            scrollbarColor: `${thumb} transparent`,
        },
        "*::-webkit-scrollbar": {
            width: 6,
            height: 6,
        },
        "*::-webkit-scrollbar-track": {
            background: "transparent",
        },
        "*::-webkit-scrollbar-thumb": {
            backgroundColor: thumb,
            borderRadius: 3,
        },
        "*::-webkit-scrollbar-thumb:hover": {
            backgroundColor: thumbHover,
        },
        // The little square where a vertical and a horizontal bar meet. Left
        // unstyled it renders as an opaque grey block against a transparent
        // track, which is the one part of a WebKit scrollbar that stays visible
        // after everything else has been quietened down.
        "*::-webkit-scrollbar-corner": {
            background: "transparent",
        },
    };
};
/**
 * The type ramp.
 *
 * Inter, with the system stack behind it, because this is a dense product full
 * of numbers: Inter's tabular figures keep a column of durations and costs
 * aligned, which Lato — the previous face — does not do, and a latency table
 * whose digits shift width between rows is genuinely harder to scan.
 *
 * Headings are tight and dark rather than large. Nothing on a working screen
 * needs a 40px title; the hierarchy is carried by weight and letter-spacing, so
 * a page header can sit close to the content it heads without shouting.
 */
const typography = {
    fontFamily: [
        "Inter",
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
    ].join(","),
    // Negative tracking on the display sizes only. Applied to body text it
    // closes up the word spacing and costs legibility at the exact sizes where
    // legibility is the whole job.
    h1: { fontSize: "2rem", fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.2 },
    h2: { fontSize: "1.625rem", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.25 },
    h3: { fontSize: "1.375rem", fontWeight: 700, letterSpacing: "-0.018em", lineHeight: 1.3 },
    h4: { fontSize: "1.1875rem", fontWeight: 650, letterSpacing: "-0.015em", lineHeight: 1.35 },
    h5: { fontSize: "1.0625rem", fontWeight: 650, letterSpacing: "-0.01em", lineHeight: 1.4 },
    h6: { fontSize: "0.9375rem", fontWeight: 650, letterSpacing: "-0.005em", lineHeight: 1.45 },
    subtitle1: { fontSize: "0.9375rem", fontWeight: 600, lineHeight: 1.5 },
    subtitle2: { fontSize: "0.8125rem", fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: "0.9375rem", lineHeight: 1.6 },
    body2: { fontSize: "0.8125rem", lineHeight: 1.6 },
    button: { fontSize: "0.875rem", fontWeight: 600, textTransform: "none" },
    caption: { fontSize: "0.75rem", lineHeight: 1.5 },
    // The section label above a group of settings: small, spaced, and quiet.
    // Uppercase is doing the work of a heading here, so it does not also need
    // size — see the sidebar's group labels, which use this verbatim.
    overline: {
        fontSize: "0.6875rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        lineHeight: 1.6,
    },
};
const lightPalette = {
    primary: {
        main: teal[600],
        light: teal[500],
        dark: teal[700],
        lighter: alpha(teal[600], 0.09),
        contrastText: "#ffffff",
    },
    secondary: {
        main: stone[600],
        light: stone[500],
        dark: stone[700],
        lighter: alpha(stone[600], 0.08),
        contrastText: "#ffffff",
    },
    success: {
        main: "#16A34A",
        light: "#22C55E",
        dark: "#15803D",
        lighter: alpha("#16A34A", 0.1),
        contrastText: "#ffffff",
    },
    error: {
        main: "#DC2626",
        light: "#EF4444",
        dark: "#B91C1C",
        lighter: alpha("#DC2626", 0.1),
        contrastText: "#ffffff",
    },
    warning: {
        main: "#D97706",
        light: "#F59E0B",
        dark: "#B45309",
        lighter: alpha("#D97706", 0.11),
        contrastText: "#ffffff",
    },
    info: {
        main: "#0891B2",
        light: "#06B6D4",
        dark: "#0E7490",
        lighter: alpha("#0891B2", 0.1),
        contrastText: "#ffffff",
    },
    background: { default: "#F8F8F6", paper: "#FFFFFF" },
    surface: {
        canvas: "#F8F8F6",
        sunken: stone[100],
        chrome: "#FFFFFF",
        border: stone[200],
        borderStrong: stone[300],
    },
    text: { primary: stone[900], secondary: stone[500], disabled: stone[400] },
    divider: stone[200],
    action: {
        hover: alpha(stone[900], 0.04),
        selected: alpha(teal[600], 0.09),
        focus: alpha(teal[600], 0.14),
    },
};
const darkPalette = {
    primary: {
        main: teal[400],
        light: teal[300],
        dark: teal[500],
        lighter: alpha(teal[400], 0.16),
        // Teal 400 is a light colour; white text on it fails contrast, so the
        // label on a filled primary button goes dark in this mode.
        contrastText: stone[950],
    },
    secondary: {
        main: stone[400],
        light: stone[300],
        dark: stone[500],
        lighter: alpha(stone[400], 0.14),
        contrastText: stone[950],
    },
    success: {
        main: "#4ADE80",
        light: "#86EFAC",
        dark: "#22C55E",
        lighter: alpha("#4ADE80", 0.16),
        contrastText: stone[950],
    },
    error: {
        main: "#F87171",
        light: "#FCA5A5",
        dark: "#EF4444",
        lighter: alpha("#F87171", 0.16),
        contrastText: stone[950],
    },
    warning: {
        main: "#FBBF24",
        light: "#FCD34D",
        dark: "#F59E0B",
        lighter: alpha("#FBBF24", 0.16),
        contrastText: stone[950],
    },
    info: {
        main: "#22D3EE",
        light: "#67E8F9",
        dark: "#06B6D4",
        lighter: alpha("#22D3EE", 0.16),
        contrastText: stone[950],
    },
    // Warm, not blue-black. The same reasoning as the light canvas: a cool dark
    // under a teal accent pulls the accent green.
    background: { default: "#121110", paper: "#1C1A19" },
    surface: {
        canvas: "#121110",
        sunken: "#191716",
        chrome: "#1C1A19",
        border: "#2E2A28",
        borderStrong: "#403A37",
    },
    text: { primary: stone[50], secondary: stone[400], disabled: stone[600] },
    divider: "#2E2A28",
    action: {
        hover: alpha(stone[50], 0.06),
        selected: alpha(teal[400], 0.16),
        focus: alpha(teal[400], 0.2),
    },
};
/**
 * Creates a theme with the specified mode (light or dark)
 * Preserves all component overrides and customizations
 */
export const createAppTheme = (mode = "light") => {
    const p = mode === "light" ? lightPalette : darkPalette;
    const isLight = mode === "light";
    // Built in two passes: the component overrides below need to read finished
    // palette values (`theme.palette.surface.border` and friends), and those do
    // not exist until `createTheme` has run once.
    const base = createTheme({
        typography,
        shape: { borderRadius: 10 },
        spacing: 8,
        shadows: buildShadows(mode),
        palette: { mode, ...p },
    });
    return createTheme(base, {
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    ...scrollbarStyles(mode),
                    // Inter's default figures are proportional, which lets the digits
                    // in a column of durations shift width from row to row. Tabular
                    // figures are the reason this font was chosen; they have to be
                    // asked for explicitly.
                    html: {
                        // Tells the browser which way its own chrome should render.
                        // The call pages embed native `<audio>` players, and without
                        // this they stay light-on-light in dark mode — a white
                        // transport bar sitting in the middle of a dark card. Also
                        // fixes native selects, date pickers and the default form
                        // focus ring in one line.
                        colorScheme: mode,
                        fontVariantNumeric: "tabular-nums",
                        WebkitFontSmoothing: "antialiased",
                        MozOsxFontSmoothing: "grayscale",
                    },
                    body: { backgroundColor: p.surface.canvas },
                },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        textTransform: "none",
                        fontWeight: 600,
                        // MUI's default is 6px 16px, which makes every button look like
                        // a hyperlink with a box around it. Real hit targets.
                        padding: "7px 14px",
                        transition: "background-color .15s, border-color .15s, box-shadow .15s",
                    },
                    sizeSmall: { padding: "4px 10px", fontSize: "0.8125rem" },
                    sizeLarge: { padding: "10px 20px", fontSize: "0.9375rem" },
                    contained: {
                        boxShadow: base.shadows[1],
                        "&:hover": { boxShadow: base.shadows[2] },
                    },
                    outlined: {
                        borderColor: p.surface.borderStrong,
                        color: base.palette.text.primary,
                        "&:hover": {
                            borderColor: base.palette.primary.main,
                            color: base.palette.primary.main,
                            backgroundColor: base.palette.primary.lighter,
                        },
                    },
                    text: { "&:hover": { backgroundColor: base.palette.action.hover } },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: { borderRadius: 8, color: base.palette.text.secondary },
                },
            },
            MuiToggleButtonGroup: {
                styleOverrides: {
                    // A segmented control, not a row of buttons: the group is the
                    // recessed track and the selected item is the raised chip inside
                    // it. This is the one place in the app that uses a shadow to say
                    // "on" rather than "above", and it is worth it — colour alone on a
                    // two-item toggle reads as ambiguous.
                    root: {
                        backgroundColor: p.surface.sunken,
                        border: `1px solid ${p.surface.border}`,
                        borderRadius: 9,
                        padding: 3,
                        gap: 3,
                    },
                    grouped: {
                        border: "0 !important",
                        borderRadius: "7px !important",
                    },
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.8125rem",
                        padding: "5px 12px",
                        color: base.palette.text.secondary,
                        "&.Mui-selected": {
                            backgroundColor: base.palette.background.paper,
                            color: base.palette.text.primary,
                            boxShadow: base.shadows[1],
                            "&:hover": { backgroundColor: base.palette.background.paper },
                        },
                    },
                },
            },
            MuiCard: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        border: `1px solid ${p.surface.border}`,
                        backgroundImage: "none",
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    // MUI tints dark-mode Paper by elevation, which fights the warm
                    // neutrals — a raised panel comes out bluer than the card next to
                    // it. Depth here is shadow only.
                    root: { backgroundImage: "none" },
                    outlined: { borderColor: p.surface.border },
                    rounded: { borderRadius: 12 },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 16,
                        border: `1px solid ${p.surface.border}`,
                    },
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: { fontSize: "1.0625rem", fontWeight: 650, padding: "20px 24px 8px" },
                },
            },
            MuiDialogActions: {
                styleOverrides: { root: { padding: "12px 24px 20px", gap: 8 } },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        borderRadius: 10,
                        border: `1px solid ${p.surface.border}`,
                        boxShadow: base.shadows[6],
                        marginTop: 6,
                    },
                    list: { padding: 6 },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        borderRadius: 7,
                        fontSize: "0.875rem",
                        minHeight: 38,
                        "&.Mui-selected": {
                            backgroundColor: base.palette.primary.lighter,
                            color: base.palette.primary.main,
                            fontWeight: 600,
                        },
                    },
                },
            },
            MuiListItemButton: {
                styleOverrides: { root: { borderRadius: 8 } },
            },
            MuiListItemIcon: {
                styleOverrides: { root: { minWidth: 36, color: "inherit" } },
            },
            MuiPopover: {
                styleOverrides: {
                    paper: {
                        borderRadius: 12,
                        border: `1px solid ${p.surface.border}`,
                        boxShadow: base.shadows[6],
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 9,
                        backgroundColor: base.palette.background.paper,
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: p.surface.borderStrong,
                            transition: "border-color .15s",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: isLight ? stone[400] : stone[500],
                        },
                        // A focus ring rather than a thicker border. Growing the border
                        // from 1px to 2px on focus reflows the input's content by a
                        // pixel, and on a form of stacked fields that shows up as the
                        // whole column twitching as focus moves down it.
                        "&.Mui-focused": {
                            boxShadow: `0 0 0 3px ${base.palette.action.focus}`,
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderWidth: 1,
                                borderColor: base.palette.primary.main,
                            },
                        },
                    },
                    input: { padding: "10px 12px" },
                    inputSizeSmall: { padding: "8px 12px" },
                },
            },
            MuiInputLabel: {
                styleOverrides: { root: { fontSize: "0.875rem", fontWeight: 500 } },
            },
            MuiFormHelperText: {
                styleOverrides: { root: { marginLeft: 2, fontSize: "0.75rem" } },
            },
            MuiTextField: {
                defaultProps: { variant: "outlined", size: "small" },
            },
            MuiSelect: {
                styleOverrides: { select: { fontSize: "0.875rem" } },
            },
            MuiChip: {
                styleOverrides: {
                    root: { borderRadius: 7, fontWeight: 600, fontSize: "0.75rem" },
                    sizeSmall: { height: 22 },
                    label: { paddingLeft: 8, paddingRight: 8 },
                    outlined: { borderColor: p.surface.borderStrong },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    root: { minHeight: 42 },
                    indicator: { height: 2, borderRadius: 2 },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        minHeight: 42,
                        padding: "0 14px",
                        color: base.palette.text.secondary,
                        "&.Mui-selected": { color: base.palette.primary.main },
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    // The filled variants are for things that must interrupt. The
                    // standard variant is the default and is a tinted panel with a
                    // hairline in the same hue, because most alerts in this app are
                    // informational and a saturated block for every one of them trains
                    // the reader to skip all of them.
                    //
                    // The hairline is the severity colour at 24%, NOT `currentColor`.
                    // `currentColor` on this element is the message text, so the border
                    // came out at full strength and the alert read as a warning box
                    // whatever its severity said.
                    root: {
                        borderRadius: 10,
                        fontSize: "0.875rem",
                        padding: "8px 14px",
                        border: "1px solid transparent",
                    },
                    standardInfo: { borderColor: alpha(base.palette.info.main, 0.24) },
                    standardSuccess: { borderColor: alpha(base.palette.success.main, 0.24) },
                    standardWarning: { borderColor: alpha(base.palette.warning.main, 0.24) },
                    standardError: { borderColor: alpha(base.palette.error.main, 0.24) },
                    icon: { opacity: 1 },
                    message: { paddingTop: 6, paddingBottom: 6 },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        backgroundColor: isLight ? stone[900] : stone[800],
                        color: stone[50],
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        borderRadius: 7,
                        padding: "6px 10px",
                        boxShadow: base.shadows[3],
                    },
                    arrow: { color: isLight ? stone[900] : stone[800] },
                },
            },
            MuiTableHead: {
                styleOverrides: { root: { backgroundColor: p.surface.sunken } },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        borderColor: p.surface.border,
                        fontSize: "0.8125rem",
                        padding: "10px 14px",
                    },
                    head: {
                        fontWeight: 700,
                        fontSize: "0.6875rem",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: base.palette.text.secondary,
                    },
                },
            },
            MuiDivider: {
                styleOverrides: { root: { borderColor: p.surface.border } },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: { borderRadius: 99, height: 6, backgroundColor: p.surface.sunken },
                    bar: { borderRadius: 99 },
                },
            },
            MuiAvatar: {
                styleOverrides: {
                    root: { fontWeight: 600, fontSize: "0.875rem" },
                },
            },
            MuiSwitch: {
                styleOverrides: {
                    root: { width: 40, height: 24, padding: 0, borderRadius: 99 },
                    switchBase: {
                        padding: 3,
                        "&.Mui-checked": {
                            transform: "translateX(16px)",
                            color: "#fff",
                            "& + .MuiSwitch-track": {
                                opacity: 1,
                                backgroundColor: base.palette.primary.main,
                            },
                        },
                    },
                    thumb: { width: 18, height: 18, boxShadow: base.shadows[1] },
                    track: {
                        borderRadius: 99,
                        opacity: 1,
                        backgroundColor: isLight ? stone[300] : stone[700],
                    },
                },
            },
            MuiAppBar: {
                defaultProps: { elevation: 0, color: "inherit" },
                styleOverrides: {
                    root: {
                        backgroundColor: p.surface.chrome,
                        color: base.palette.text.primary,
                        borderBottom: `1px solid ${p.surface.border}`,
                        backgroundImage: "none",
                    },
                },
            },
            MuiSkeleton: {
                defaultProps: { animation: "wave" },
                styleOverrides: { root: { borderRadius: 8, backgroundColor: p.surface.sunken } },
            },
            MuiBackdrop: {
                styleOverrides: {
                    root: { backgroundColor: alpha(stone[950], isLight ? 0.4 : 0.62) },
                },
            },
        },
    });
};
/**
 * Default light theme (for backwards compatibility)
 */
export const theme = createAppTheme("light");
//# sourceMappingURL=theme.config.js.map